// First Created - 06 May 2026
// Author - Atul Pandey

import {
    createSelector,
    createEntityAdapter,
} from '@reduxjs/toolkit'
import { apiSlice } from '@/app/api/apiSlice'

const userSchedulingSlotsAdapter = createEntityAdapter({})

const initialState = userSchedulingSlotsAdapter.getInitialState()

export const userSchedulingSlotsApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getUserSchedulingSlots: builder.query({
            query: ({ schedulingPageId }) => ({
                url: '/userSchedulingSlots/getUserSchedulingSlots',
                params: { schedulingPageId },
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                }
            }),
            transformResponse: responseData => {
                const loadedUserSchedulingSlots = responseData.map(userSchedulingSlot => {
                    userSchedulingSlot.id = userSchedulingSlot._id
                    return userSchedulingSlot
                })
                return userSchedulingSlotsAdapter.setAll(initialState, loadedUserSchedulingSlots)
            },
            providesTags: (result, error, arg) => {
                if (result?.ids) {
                    return [
                        { type: 'UserSchedulingSlot', id: 'LIST' },
                        ...result.ids.map(id => ({ type: 'UserSchedulingSlot', id }))
                    ]
                } else {
                    return [{ type: 'UserSchedulingSlot', id: 'LIST' }]
                }
            },
        }),

        createNewUserSchedulingSlot: builder.mutation({
            query: initialUserSchedulingSlot => ({
                url: '/userSchedulingSlots/createNewUserSchedulingSlot',
                method: 'POST',
                body: { ...initialUserSchedulingSlot }
            }),
            invalidatesTags: [
                { type: 'UserSchedulingSlot', id: 'LIST' }
            ]
        }),

        updateUserSchedulingSlot: builder.mutation({
            query: initialUserSchedulingSlot => ({
                url: '/userSchedulingSlots/updateUserSchedulingSlot',
                method: 'PATCH',
                body: { ...initialUserSchedulingSlot }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'UserSchedulingSlot', id: arg.slotId },
                { type: 'UserSchedulingSlot', id: 'LIST' }
            ],
        }),

        deleteUserSchedulingSlot: builder.mutation({
            query: ({ slotId }) => ({
                url: '/userSchedulingSlots/deleteUserSchedulingSlot',
                method: 'DELETE',
                body: { slotId }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'UserSchedulingSlot', id: arg.slotId },
                { type: 'UserSchedulingSlot', id: 'LIST' }
            ]
        }),
        // 17 May 2026
        createCalendarEventForBookedSlot: builder.mutation({
            query: ({ slotId }) => ({
                url: '/userSchedulingSlots/createCalendarEventForBookedSlot',
                method: 'PATCH',
                body: { slotId }
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'UserSchedulingSlot', id: arg.slotId },
                { type: 'UserSchedulingSlot', id: 'LIST' }
            ],
        }),
    }),
})

export const {
    useGetUserSchedulingSlotsQuery,
    useCreateNewUserSchedulingSlotMutation,
    useUpdateUserSchedulingSlotMutation,
    useDeleteUserSchedulingSlotMutation,
    useCreateCalendarEventForBookedSlotMutation, // 17 May 2026
} = userSchedulingSlotsApiSlice

export const selectUserSchedulingSlotResult =
    userSchedulingSlotsApiSlice.endpoints.getUserSchedulingSlots.select()

const selectUserSchedulingSlotsData = createSelector(
    selectUserSchedulingSlotResult,
    userSchedulingSlotsResult => userSchedulingSlotsResult.data
)

export const {
    selectAll: selectAllUserSchedulingSlots,
    selectById: selectUserSchedulingSlotById,
    selectIds: selectUserSchedulingSlotIds,
} = userSchedulingSlotsAdapter.getSelectors(
    state => selectUserSchedulingSlotsData(state) ?? initialState
)